import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import AppShell from "../components/AppShell";
import { FUNDER_NAV } from "./FunderTerminal";
import { toast } from "sonner";
import { showOversizedFileToast } from "../lib/uploadToast";
import {
  Globe2, Upload, FileText, Sparkles, Loader2, ArrowRight, CheckCircle2,
  ChevronLeft, X,
} from "lucide-react";
import { sdgLabel } from "../lib/sdg";

const SECTORS = ["Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics", "E-commerce", "Manufacturing", "Edtech", "Hospitality"];
const COUNTRIES = ["Nigeria","Kenya","Ghana","South Africa","Egypt","Rwanda","Senegal","Ethiopia","Tanzania","Uganda"];
const SDGS = ["SDG 2","SDG 3","SDG 4","SDG 5","SDG 7","SDG 8","SDG 9","SDG 13"];
const INSTRUMENTS = [
  { id: "equity", label: "Equity" },
  { id: "debt", label: "Debt" },
  { id: "convertible", label: "Convertible Note / SAFE" },
  { id: "mezzanine", label: "Mezzanine / Quasi-equity" },
  { id: "grant", label: "Grant (non-repayable)" },
  { id: "mix", label: "Mix (Equity + Debt)" },
  { id: "open", label: "Open to all structures" },
];
const DEAL_FOCUS = [
  { id: "businesses", label: "Businesses" },
  { id: "projects", label: "Projects" },
  { id: "both", label: "Both" },
];
const DEAL_FOCUS_LABEL = { businesses: "Businesses", projects: "Projects", both: "Businesses & Projects" };
const RISK_LABEL = { bold: "The Bold Builder — higher risk, higher return", balanced: "The Balanced Investor — medium risk", steady: "The Steady Earner — lower risk" };

/* Funder first-login flow:
 *   Step 1 — Source: paste a website URL, upload a fund-memo, OR paste a thesis
 *   Step 2 — Review & tweak the AI-extracted mandate
 *   Step 3 — Confirm — saves the mandate AND flags onboarding complete
 *
 * After confirm we route them to /funder/terminal which then only surfaces
 * deals that match their mandate (compat >= 40).
 */
export default function FunderOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ loaded: false, confirmed: false });
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("url"); // 'url' | 'upload' | 'thesis'
  const [url, setUrl] = useState("");
  const [thesis, setThesis] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mandate, setMandate] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [confidence, setConfidence] = useState(null);
  // Quiz-captured mandate (prefilled at signup from /get-started-funder).
  const [quiz, setQuiz] = useState(null);
  const [snapDismissed, setSnapDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/funder/onboarding-status");
        setStatus({ loaded: true, confirmed: !!data.mandate_confirmed });
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[FunderOnboarding] status fetch failed", err);
        setStatus({ loaded: true, confirmed: false });
      }
      try {
        const { data: m } = await api.get("/funder/mandate");
        const meaningful = (m.sectors || []).length || (m.countries || []).length ||
          (m.instruments || []).length || m.investor_type;
        if (meaningful) setQuiz(m);
      } catch (_e) { /* no prefilled mandate — fall through to the AI wizard */ }
    })();
  }, []);

  // Already onboarded → skip the wizard.
  if (status.loaded && status.confirmed) {
    return <Navigate to="/funder/terminal" replace />;
  }
  if (!user || user.role !== "funder") {
    return <Navigate to="/login" replace />;
  }

  const runAi = async () => {
    setBusy(true); setErr("");
    // Persistent loading toast — covers slow uploads + LLM extraction so the
    // funder always sees that something is happening (matches the founder-side
    // upload UX). Resolves to success/error toast on completion.
    const tLabel =
      mode === "upload" ? `Reading ${file?.name || "your document"}…`
      : mode === "url" ? "Fetching your firm's website…"
      : "Extracting your investment thesis…";
    const tId = toast.loading(tLabel, {
      description: "Sending to Clinq — our AI is reading it now.",
      duration: Infinity,
    });
    try {
      let data;
      if (mode === "url") {
        if (!url.trim()) throw new Error("Please enter your firm's website URL.");
        ({ data } = await api.post("/funder/mandate/from-url", { url: url.trim() }));
      } else if (mode === "upload") {
        if (!file) throw new Error("Please attach a PDF or text document.");
        if (file.size > 15 * 1024 * 1024) {
          // Surface an actionable rescue toast with a one-click "Compress now"
          // link before the throw — keeps the funder from bouncing out.
          showOversizedFileToast(file, 15);
          throw new Error("Your file is over the 15 MB limit. Try a slimmer export.");
        }
        const fd = new FormData(); fd.append("file", file);
        ({ data } = await api.post("/funder/mandate/from-upload", fd,
          { timeout: 120000 }));
      } else {
        if (thesis.trim().length < 40) throw new Error("Paste at least a paragraph of your investment thesis.");
        ({ data } = await api.post("/funder/mandate/ai-suggest", { text: thesis.trim() }));
      }
      setMandate({
        sectors: data.sectors || [], countries: data.countries || [],
        stages: data.stages || [], sdgs: data.sdgs || [],
        ticket_min: data.ticket_min || 50000,
        ticket_max: data.ticket_max || 2000000,
        min_readiness: data.min_readiness || 0,
        instruments: (quiz?.instruments?.length ? quiz.instruments : data.instruments) || [],
        investor_type: quiz?.investor_type || data.investor_type || null,
        deal_focus: quiz?.deal_focus || data.deal_focus || null,
        risk_appetite: quiz?.risk_appetite || data.risk_appetite || null,
      });
      setAiSummary(data.summary || "");
      setConfidence(data.confidence ?? null);
      setStep(2);
      toast.success("Mandate extracted — review and confirm.", { id: tId, duration: 4000 });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Couldn't auto-generate your mandate. Please try again.";
      setErr(msg);
      toast.error(msg, { id: tId });
    } finally { setBusy(false); }
  };

  const toggle = (key, val) =>
    setMandate(m => ({ ...m, [key]: m[key].includes(val) ? m[key].filter(x=>x!==val) : [...m[key], val] }));

  const missingKeys = () => {
    const miss = [];
    if (!mandate?.investor_type) miss.push("Investing as");
    if (!mandate?.deal_focus) miss.push("Deal focus");
    if (!(mandate?.instruments || []).length) miss.push("Structures you offer");
    if (!mandate?.ticket_min && !mandate?.ticket_max) miss.push("Ticket size");
    return miss;
  };
  const confirm = async () => {
    const miss = missingKeys();
    if (miss.length) { setErr(`Please complete: ${miss.join(", ")} — funders are matched on these.`); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/funder/mandate/confirm", mandate);
      await refresh();
      setStep(3);
      setTimeout(() => navigate("/funder/terminal"), 1500);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't save your mandate. Please try again.");
    } finally { setBusy(false); }
  };

  const quizToMandate = () => ({
    sectors: quiz.sectors || [], countries: quiz.countries || [],
    stages: quiz.stages || [], sdgs: quiz.sdgs || [],
    ticket_min: quiz.ticket_min || 50000,
    ticket_max: quiz.ticket_max || 2000000,
    min_readiness: quiz.min_readiness || 0,
    instruments: quiz.instruments || [],
    risk_appetite: quiz.risk_appetite || null,
    investor_type: quiz.investor_type || null,
    deal_focus: quiz.deal_focus || null,
  });

  const confirmSnapshot = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/funder/mandate/confirm", quizToMandate());
      await refresh();
      setStep(3);
      setTimeout(() => navigate("/funder/terminal"), 1500);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't save your mandate. Please try again.");
    } finally { setBusy(false); }
  };

  const editSnapshot = () => {
    setMandate(quizToMandate());
    setStep(2);
  };

  return (
    <AppShell nav={FUNDER_NAV}>
      <div className="mx-auto max-w-3xl">
        <Steps current={step} />

        {step === 1 && quiz && !snapDismissed && (
          <Card title="Your investor snapshot" subtitle="Here's your current investor profile. Confirm it to see matching deals right away — or fine-tune it first.">
            <div className="mt-5 rounded-xl border border-[#0A8B85]/25 bg-[#F0F7F6] p-5" data-testid="onb-quiz-snapshot">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {quiz.investor_type && (
                  <SnapRow label="Investing as">
                    <span className="inline-block rounded-full bg-[#0A8B85] px-3 py-1 text-xs font-bold text-white" data-testid="onb-snap-investor-type">
                      {quiz.investor_type === "institution" ? "Institution" : "Individual/Group"}
                    </span>
                  </SnapRow>
                )}
                {quiz.deal_focus && (
                  <SnapRow label="Deal focus">
                    <span className="text-sm font-bold text-neutral-900" data-testid="onb-snap-deal-focus">
                      {DEAL_FOCUS_LABEL[quiz.deal_focus] || quiz.deal_focus}
                    </span>
                  </SnapRow>
                )}
                <SnapRow label="Ticket size">
                  <span className="text-sm font-bold text-neutral-900" data-testid="onb-snap-ticket">
                    ${Number(quiz.ticket_min || 0).toLocaleString()} – ${Number(quiz.ticket_max || 0).toLocaleString()}
                  </span>
                </SnapRow>
                {(quiz.sectors || []).length > 0 && (
                  <SnapRow label="Sectors"><SnapChips vals={quiz.sectors} testid="onb-snap-sectors" /></SnapRow>
                )}
                {(quiz.countries || []).length > 0 && (
                  <SnapRow label="Geography"><SnapChips vals={quiz.countries} testid="onb-snap-countries" /></SnapRow>
                )}
                {(quiz.stages || []).length > 0 && (
                  <SnapRow label="Stages"><SnapChips vals={quiz.stages} testid="onb-snap-stages" /></SnapRow>
                )}
                {quiz.risk_appetite && (
                  <SnapRow label="Risk appetite">
                    <span className="text-sm font-bold text-neutral-900" data-testid="onb-snap-risk">
                      {RISK_LABEL[quiz.risk_appetite] || quiz.risk_appetite}
                    </span>
                  </SnapRow>
                )}
                {(quiz.instruments || []).length > 0 && (
                  <SnapRow label="Structures">
                    <SnapChips vals={(quiz.instruments || []).map(i => i.charAt(0).toUpperCase() + i.slice(1))} testid="onb-snap-instruments" />
                  </SnapRow>
                )}
              </div>
            </div>

            {err && <p className="mt-4 text-sm text-rose-600" data-testid="onb-err">{err}</p>}

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <button type="button" data-testid="onb-snap-rebuild" onClick={() => setSnapDismissed(true)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-500 hover:text-neutral-800">
                <Sparkles className="h-4 w-4" /> Rebuild with AI instead
              </button>
              <div className="flex flex-wrap items-center gap-3">
                <button type="button" data-testid="onb-snap-edit" onClick={editSnapshot}
                  className="rounded-lg border border-[#0A8B85] bg-white px-5 py-2.5 text-sm font-bold text-[#0A8B85] hover:bg-[#F0F7F6]">
                  Edit details
                </button>
                <button type="button" data-testid="onb-snap-confirm" onClick={confirmSnapshot} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin"/> Saving…</> : <>Looks right — show me deals <ArrowRight className="h-4 w-4"/></>}
                </button>
              </div>
            </div>
          </Card>
        )}

        {step === 1 && (!quiz || snapDismissed) && (
          <Card title="Tell us where you invest" subtitle="Drop your firm's website (recommended), upload a fund memo / LP deck, or paste your investment thesis. Clinq AI will auto-build your mandate in ~10 seconds — or skip and fill it in manually.">
            {quiz && snapDismissed && (
              <button type="button" data-testid="onb-back-to-snapshot"
                onClick={() => setSnapDismissed(false)}
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#0A8B85] hover:underline">
                &larr; Back to my snapshot
              </button>
            )}
            <p className="mt-2 text-[11px] text-neutral-400">
              You can also run this AI builder any time later from your Mandate page (&ldquo;Auto-fill from thesis&rdquo;).
            </p>
            {/* Mode tabs */}
            <div className="mt-5 flex gap-1 rounded-lg border border-neutral-200 bg-white p-1" data-testid="onb-mode-tabs">
              <TabBtn active={mode==="url"} onClick={()=>setMode("url")} testid="onb-mode-url" icon={Globe2}>Website URL</TabBtn>
              <TabBtn active={mode==="upload"} onClick={()=>setMode("upload")} testid="onb-mode-upload" icon={Upload}>Upload doc</TabBtn>
              <TabBtn active={mode==="thesis"} onClick={()=>setMode("thesis")} testid="onb-mode-thesis" icon={FileText}>Paste thesis</TabBtn>
            </div>

            {mode === "url" && (
              <div className="mt-5">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Firm website</label>
                <input data-testid="onb-url" type="text" value={url} onChange={e=>setUrl(e.target.value)}
                  placeholder="https://yourfund.com" className={inp + " mt-1"} />
                <p className="mt-1.5 text-xs text-neutral-500">We&apos;ll read your homepage and About / Strategy pages.</p>
              </div>
            )}

            {mode === "upload" && (
              <div className="mt-5">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Upload PDF / text doc</label>
                <input type="file" accept=".pdf,.txt,.md" data-testid="onb-file"
                  onChange={e => setFile(e.target.files?.[0] || null)}
                  className="mt-2 block w-full text-sm text-neutral-700 file:mr-3 file:rounded-md file:border-0 file:bg-[#0A8B85] file:px-4 file:py-2 file:text-sm file:font-bold file:text-white hover:file:bg-[#066667]" />
                {file && <p className="mt-2 text-xs text-neutral-600">Selected: {file.name} ({Math.round(file.size/1024)} KB)</p>}
              </div>
            )}

            {mode === "thesis" && (
              <div className="mt-5">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Investment thesis</label>
                <textarea data-testid="onb-thesis" rows={6} value={thesis} onChange={e=>setThesis(e.target.value)}
                  placeholder="e.g. We are a $50M debt fund focused on West Africa, primarily Nigeria and Ghana. We back working-capital revenue-stage SMEs in fintech, e-commerce and logistics. Ticket: $300K–$1.2M. We prioritise gender-lens (Gender) and clean-energy themes…"
                  className={inp + " mt-1 resize-none"}/>
              </div>
            )}

            {err && <p className="mt-4 text-sm text-rose-600" data-testid="onb-err">{err}</p>}

            <div className="mt-6 flex items-center justify-between gap-3">
              <button type="button" data-testid="onb-manual"
                onClick={() => { setMandate({ sectors: [], countries: [], stages: [], sdgs: [], ticket_min: 50000, ticket_max: 2000000, min_readiness: 0, instruments: [], investor_type: null, deal_focus: null, risk_appetite: null }); setAiSummary(""); setStep(2); }}
                className="text-sm font-semibold text-neutral-600 underline-offset-4 hover:text-[#0A8B85] hover:underline">
                Skip — fill in manually
              </button>
              <button data-testid="onb-extract" onClick={runAi} disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin"/> Reading…</> : <><Sparkles className="h-4 w-4"/> Auto-build my mandate</>}
              </button>
            </div>
          </Card>
        )}

        {step === 2 && mandate && (
          <Card title="Review your mandate" subtitle="Adjust anything that doesn't look right. We'll only show you deals that match this mandate (≥40% compatibility).">
            {aiSummary && (
              <div className="mt-4 rounded-xl bg-[#F4F8F8] p-4 text-sm text-neutral-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#0A8B85]">AI summary</span>
                  {confidence != null && <span className="rounded-full bg-[#0A8B85]/10 px-2 py-0.5 text-[10px] font-bold text-[#0A8B85]">Confidence {confidence}</span>}
                </div>
                <p className="mt-1.5">{aiSummary}</p>
              </div>
            )}

            <Group label="Investing as">
              <div className="flex flex-wrap gap-2">
                {[{ id: "individual", label: "Individual/Group" }, { id: "institution", label: "Institution" }].map(o => {
                  const on = mandate.investor_type === o.id;
                  return <button key={o.id} type="button" data-testid={`onb-investor-${o.id}`}
                    onClick={() => setMandate({ ...mandate, investor_type: o.id })}
                    className={"rounded-full border px-3 py-1 text-xs " +
                      (on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700")}>
                    {o.label}
                  </button>;
                })}
              </div>
            </Group>
            <Group label="Deal focus">
              <div className="flex flex-wrap gap-2">
                {DEAL_FOCUS.map(o => {
                  const on = mandate.deal_focus === o.id;
                  return <button key={o.id} type="button" data-testid={`onb-dealfocus-${o.id}`}
                    onClick={() => setMandate({ ...mandate, deal_focus: o.id })}
                    className={"rounded-full border px-3 py-1 text-xs " +
                      (on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700")}>
                    {o.label}
                  </button>;
                })}
              </div>
            </Group>
            <Group label="Risk appetite">
              <div className="flex flex-wrap gap-2">
                {Object.entries(RISK_LABEL).map(([id, label]) => {
                  const on = mandate.risk_appetite === id;
                  return <button key={id} type="button" data-testid={`onb-risk-${id}`}
                    onClick={() => setMandate({ ...mandate, risk_appetite: id })}
                    className={"rounded-full border px-3 py-1 text-xs " +
                      (on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700")}>
                    {label}
                  </button>;
                })}
              </div>
            </Group>
            <Group label="Structures">
              <div className="flex flex-wrap gap-2">
                {INSTRUMENTS.map(o => {
                  const on = (mandate.instruments || []).includes(o.id);
                  return <button key={o.id} type="button" data-testid={`onb-instrument-${o.id}`}
                    onClick={() => setMandate(m => ({ ...m, instruments: on ? m.instruments.filter(x => x !== o.id) : [...(m.instruments || []), o.id] }))}
                    className={"rounded-full border px-3 py-1 text-xs " +
                      (on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700")}>
                    {o.label}
                  </button>;
                })}
              </div>
            </Group>
            <Group label="Sectors"><Chips opts={[...new Set([...SECTORS, ...(mandate.sectors||[])])]} selected={mandate.sectors} onToggle={v=>toggle("sectors",v)} testPrefix="onb-sector"/></Group>
            <Group label="Countries"><Chips opts={[...new Set([...COUNTRIES, ...(mandate.countries||[])])]} selected={mandate.countries} onToggle={v=>toggle("countries",v)} testPrefix="onb-country"/></Group>
            <Group label="Impact areas"><Chips opts={SDGS} selected={mandate.sdgs} onToggle={v=>toggle("sdgs",v)} renderLabel={sdgLabel} testPrefix="onb-sdg"/></Group>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Ticket min (USD)">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">$</span>
                  <input data-testid="onb-tmin" type="number" value={mandate.ticket_min} onChange={e=>setMandate({...mandate,ticket_min:+e.target.value})} className={inp + " pl-7"}/>
                </div>
              </Field>
              <Field label="Ticket max (USD)">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">$</span>
                  <input data-testid="onb-tmax" type="number" value={mandate.ticket_max} onChange={e=>setMandate({...mandate,ticket_max:+e.target.value})} className={inp + " pl-7"}/>
                </div>
              </Field>
            </div>

            {err && <p className="mt-4 text-sm text-rose-600">{err}</p>}

            <div className="mt-6 flex items-center justify-between">
              <button onClick={()=>setStep(1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-neutral-900" data-testid="onb-back">
                <ChevronLeft className="h-4 w-4"/> Back
              </button>
              <button data-testid="onb-confirm" onClick={confirm} disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin"/> Saving…</> : <>Confirm mandate <ArrowRight className="h-4 w-4"/></>}
              </button>
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card title="You're all set" subtitle="Mandate locked. Taking you to your filtered deal terminal…">
            <div className="my-8 flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0A8B85]/15 text-[#0A8B85]">
                <CheckCircle2 className="h-8 w-8"/>
              </div>
              <p className="mt-4 text-lg font-bold text-neutral-900">Welcome to Clinq.</p>
              <p className="mt-1 text-sm text-neutral-600">Redirecting…</p>
            </div>
          </Card>
        )}
      </div>
    </AppShell>
  );
}

const Steps = ({ current }) => (
  <div className="mb-6 flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wider" data-testid="onb-stepper">
    {["Source", "Review", "Confirm"].map((label, i) => {
      const n = i + 1;
      const done = current > n, active = current === n;
      return (
        <div key={label} className="flex items-center gap-2">
          <span className={"flex h-7 w-7 items-center justify-center rounded-full text-[11px] " +
            (done ? "bg-[#0A8B85] text-white" : active ? "bg-[#0A8B85]/15 text-[#0A8B85] ring-2 ring-[#0A8B85]" : "bg-neutral-200 text-neutral-500")}>
            {done ? <CheckCircle2 className="h-3.5 w-3.5"/> : n}
          </span>
          <span className={done || active ? "text-[#0A8B85]" : "text-neutral-400"}>{label}</span>
          {n < 3 && <span className="mx-1 hidden h-px w-8 bg-neutral-300 sm:block"/>}
        </div>
      );
    })}
  </div>
);

const SnapRow = ({ label, children }) => (
  <div>
    <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[#0A8B85]">{label}</p>
    {children}
  </div>
);

const SnapChips = ({ vals, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {vals.map(v => (
      <span key={v} className="rounded-full border border-[#0A8B85]/30 bg-white px-2.5 py-0.5 text-xs font-semibold text-neutral-800">{v}</span>
    ))}
  </div>
);

const Card = ({ title, subtitle, children }) => (
  <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
    <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h1>
    {subtitle && <p className="mt-1.5 text-sm text-neutral-600">{subtitle}</p>}
    {children}
  </div>
);

const TabBtn = ({ active, onClick, icon: Icon, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={"flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors " +
      (active ? "bg-[#0A8B85] text-white shadow-sm" : "text-neutral-600 hover:bg-neutral-50")}>
    <Icon className="h-4 w-4"/> {children}
  </button>
);

const Group = ({ label, children }) => (
  <div className="mt-5">
    <p className="mb-2 text-xs font-bold uppercase tracking-wider text-neutral-500">{label}</p>
    {children}
  </div>
);

const Chips = ({ opts, selected, onToggle, renderLabel, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {opts.map(o => {
      const on = selected.includes(o);
      return <button key={o} type="button" data-testid={`${testPrefix}-${o.replace(/\s+/g,'')}`}
        onClick={() => onToggle(o)}
        className={"rounded-full border px-3 py-1 text-xs " +
          (on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700")}>
        {renderLabel ? renderLabel(o) : o}
      </button>;
    })}
  </div>
);

const inp = "w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A8B85]";
const Field = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
    {children}
  </div>
);
